import { useCallback, useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCatalog } from "@/context/CatalogContext";
import { catalogIcon } from "@/lib/catalogIcons";
import { api } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  ClipboardList,
  AlertTriangle,
  FolderTree,
  Sparkles,
  Users as UsersIcon,
  Building2,
  Bell,
  Sun,
  Moon,
  LogOut,
  Menu,
  X,
  Bug,
  Settings,
  ScrollText,
  Database,
  Palette,
  UserCheck,
  ClipboardCheck,
  FlaskConical,
  SlidersHorizontal,
  KeyRound,
  Gauge,
  Zap,
  FileText,
  Map as MapIcon,
  Layers,
  Wheat,
  CloudSun,
  Mail as MailIcon,
  Cog,
  ShieldCheck,
  Brush,
  Cpu,
  Check,
  ChevronDown,
} from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ClientSelector from "@/components/ClientSelector";
import useRealTimeEngine from "@/hooks/useRealTimeEngine";

// ================================================================
// NAVEGAÇÃO
// ================================================================

// Menu dirigido pelo Catálogo de Módulos (authorization/catalog.py via
// GET /api/catalog/me): nenhuma lista local de módulos/permissões.
const ADMIN_CENTER_ENTRIES = ["gestao_acessos", "empresas", "catalogo_modulos", "design_dashboard"];

function catalogItem(entry) {
  return {
    key: entry.key,
    to: entry.route,
    label: entry.label,
    desc: entry.description,
    icon: catalogIcon(entry.icon),
  };
}

// ================================================================
// GRUPO DE NAVEGAÇÃO
// ================================================================
//
// Este componente estava sendo utilizado pelo Layout, porém não
// estava definido no arquivo. Isso causava:
//
// ReferenceError: NavGroup is not defined
//
// ================================================================

function NavGroup({
  title,
  items,
  onNavigate,
}) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      <p className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {title}
      </p>

      {items.map(
        ({
          to,
          label,
          icon: Icon,
        }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground",
              ].join(" ")
            }
            data-testid={`nav-${label
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(
                /[^a-z0-9-]/g,
                "",
              )}`}
          >
            {Icon && (
              <Icon
                size={17}
                className="shrink-0"
              />
            )}

            <span className="truncate">
              {label}
            </span>
          </NavLink>
        ),
      )}
    </div>
  );
}

// ================================================================
// SELETOR DE CLIENTE
// ================================================================

// ================================================================
// CLIENT SELECTOR — importado de @/components/ClientSelector
// ================================================================
// O seletor global de cliente foi movido para ClientSelector.jsx
// e é importado no início do arquivo. Ele consome ClientContext
// diretamente e não duplica lógica.

// ================================================================
// MOTOR CENTRAL DE NOTIFICAÇÕES
// ================================================================
//
// O transporte SSE, polling silencioso, reconexão, som, voz e
// notificações nativas ficam centralizados no useRealTimeEngine.
// O Layout permanece responsável pela apresentação e navegação.
//
// SSE: entrega praticamente imediata.
// Polling: fallback silencioso, com 5 segundos por padrão.
//
// ================================================================

// ================================================================
// NOTIFICAÇÃO — APRESENTAÇÃO
// ================================================================

const NOTIFICATION_PRIORITY = {
  critical: { label: "Crítica", className: "border-red-500/40 bg-red-500/10", dot: "bg-red-500", text: "text-red-600 dark:text-red-400" },
  high: { label: "Alta", className: "border-orange-500/40 bg-orange-500/10", dot: "bg-orange-500", text: "text-orange-600 dark:text-orange-400" },
  medium: { label: "Média", className: "border-amber-500/40 bg-amber-500/10", dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
  low: { label: "Baixa", className: "border-sky-500/40 bg-sky-500/10", dot: "bg-sky-500", text: "text-sky-600 dark:text-sky-400" },
};

function notificationPriority(notification) {
  const raw = String(notification?.priority || notification?.severity || notification?.criticality || "").trim().toLowerCase();
  if (["critical", "critica", "crítica", "critico", "crítico"].includes(raw)) return "critical";
  if (["high", "alta"].includes(raw)) return "high";
  if (["medium", "media", "média"].includes(raw)) return "medium";
  return "low";
}

function sortNotifications(items) {
  const order = { critical: 0, high: 1, medium: 2, low: 3 };
  return [...items].sort((a, b) => {
    const pa = order[notificationPriority(a)];
    const pb = order[notificationPriority(b)];
    if (pa !== pb) return pa - pb;
    return String(b?.created_at || "").localeCompare(String(a?.created_at || ""));
  });
}

// ================================================================
// LAYOUT
// ================================================================

export default function Layout({
  theme,
  setTheme,
}) {
  const {
    user,
    logout,
    refreshAuthorization,
  } = useAuth();

  const {
    branding,
    applyUserTheme,
  } = useBranding();

  const navigate = useNavigate();
  const location = useLocation();

  // Menu recolhido por padrão: cada tela ocupa toda a largura.
  // O drawer abre somente quando solicitado e fecha ao navegar.
  const [open, setOpen] = useState(false);

  const [notifications, setNotifications] =
    useState([]);

  const [demo, setDemo] =
    useState(null);

    const [pwdOpen, setPwdOpen] =
    useState(false);

  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);

  // Menu da engrenagem (Configurações): controlado para fechar ao navegar,
  // ao selecionar item, ao clicar fora (shadcn) e ao pressionar Escape (shadcn).
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);

  // Fecha o menu da engrenagem sempre que a rota muda.
  useEffect(() => {
    setSettingsMenuOpen(false);
  }, [location.pathname]);

  const [notificationSystemPermission, setNotificationSystemPermission] =
    useState(
      typeof window !== "undefined" &&
      "Notification" in window
        ? Notification.permission
        : "unsupported",
    );

  // ==============================================================
  // NOTIFICAÇÕES
  // ==============================================================

  const loadNotifications =
    useCallback(async () => {
      try {
        const { data } =
          await api.get(
            "/notifications",
          );

        if (!Array.isArray(data)) {
          return;
        }

        setNotifications(sortNotifications(data).slice(0, 50));
      } catch {
        // Falha silenciosa. O motor continua pelo SSE/polling.
      }
    }, []);

  const markNotificationRead =
    useCallback(
      async (notificationId) => {
        if (!notificationId) {
          return false;
        }

        try {
          await api.patch(
            `/notifications/${notificationId}/read`,
          );

          setNotifications(
            (current) =>
              current.map(
                (notification) =>
                  notification.id ===
                  notificationId
                    ? {
                        ...notification,
                        read: true,
                      }
                    : notification,
              ),
          );

          return true;
        } catch (error) {
          console.error(
            "Falha ao marcar notificação como lida:",
            error,
          );

          return false;
        }
      },
      [],
    );

  const handleNotificationClick =
    useCallback(
      async (notification) => {
        if (!notification) {
          return;
        }

        const wasMarked =
          notification.read
            ? true
            : await markNotificationRead(
                notification.id,
              );

        if (!wasMarked) {
          toast.error(
            "Não foi possível marcar a notificação como lida.",
          );
        }

        if (
          notification.occurrence_id
        ) {
          navigate(
            `/ocorrencias/${notification.occurrence_id}`,
          );
          return;
        }

        if (
          notification.entity_type &&
          notification.entity_id
        ) {
          return;
        }
      },
      [
        markNotificationRead,
        navigate,
      ],
    );

  const handleRealtimeNotification =
    useCallback(
      (detail) => {
        const notification =
          detail?.notification ||
          detail?.data?.notification ||
          (
            detail?.title ||
            detail?.body ||
            detail?.message
              ? detail
              : null
          );

        if (!notification?.id) {
          return;
        }

        setNotifications(
          (current) => {
            const exists =
              current.some(
                (item) =>
                  item.id ===
                  notification.id,
              );

            if (exists) {
              return current;
            }

            return sortNotifications([
              notification,
              ...current,
            ]).slice(0, 50);
          },
        );
      },
      [],
    );

  // ==============================================================
  // MOTOR CENTRAL DE TEMPO REAL
  // ==============================================================
  //
  // Existe uma única conexão SSE no Layout.
  // O motor também mantém polling silencioso de fallback,
  // reconexão automática e emissão de eventos globais.
  //

  const {
    connected: realtimeConnected,
    notificationPermission,
    requestBrowserPermission,
    testNotification,
  } = useRealTimeEngine({
    enabled: true,
    onNotification:
      handleRealtimeNotification,
    pollingInterval: 5000,
  });

  useEffect(() => {
    setNotificationSystemPermission(
      notificationPermission,
    );
  }, [notificationPermission]);

  useEffect(() => {
    loadNotifications();

    const refresh =
      () => loadNotifications();

    window.addEventListener(
      "astral:refresh",
      refresh,
    );

    return () => {
      window.removeEventListener(
        "astral:refresh",
        refresh,
      );
    };
  }, [loadNotifications]);

  const handleRequestBrowserPermission =
    useCallback(async () => {
      const result =
        await requestBrowserPermission();

      setNotificationSystemPermission(
        result,
      );

      if (result === "granted") {
        toast.success(
          "Notificações do sistema ativadas.",
        );
      } else if (result === "denied") {
        toast.error(
          "O navegador bloqueou as notificações. Libere a permissão nas configurações do site.",
        );
      }

      return result;
    }, [
      requestBrowserPermission,
    ]);

  const handleTestNotification =
    useCallback(async () => {
      await testNotification();
      await loadNotifications();
    }, [
      testNotification,
      loadNotifications,
    ]);

  const loadDemo = useCallback(
    () =>
      api
        .get("/demo/status")
        .then(({ data }) =>
          setDemo(data),
        )
        .catch(() => {}),
    [],
  );

  useEffect(() => {
    loadDemo();

    api
      .get("/themes/my-preference")
      .then(({ data }) => {
        if (!data.allowed) {
          return;
        }

        if (data.theme) {
          applyUserTheme(
            data.theme,
          );
        }

        if (data.mode) {
          setTheme(data.mode);
        }
      })
      .catch(() => {});

    window.addEventListener(
      "demo-changed",
      loadDemo,
    );

    return () => {
      window.removeEventListener(
        "demo-changed",
        loadDemo,
      );
    };
  }, [
    applyUserTheme,
    loadDemo,
    setTheme,
  ]);

  // Permite que o PlatformAdmin sinalize uma alteração de autorização
  // sem recarregar a página inteira.
  useEffect(() => {
    const refreshModules = () => {
      void refreshAuthorization(true);
    };

    window.addEventListener(
      "astral:modules-changed",
      refreshModules,
    );

    return () => {
      window.removeEventListener(
        "astral:modules-changed",
        refreshModules,
      );
    };
  }, [refreshAuthorization]);

  // ==============================================================
  // CONTADORES
  // ==============================================================

  const demoActive =
    !!demo?.active;

  const unread =
    notifications.filter(
      (notification) =>
        !notification.read,
    ).length;

  // ==============================================================
  // VISIBILIDADE DOS MÓDULOS — Catálogo (backend decide)
  // ==============================================================

  const { byCategory, isAvailable } = useCatalog();

  const operacao = byCategory("operacional").map(catalogItem);
  const estrutura = byCategory("estrutural").map(catalogItem);
  const configItems = byCategory("configuracao").map(catalogItem);
  const showAdminCenter = ADMIN_CENTER_ENTRIES.some((key) => isAvailable(key));

  // ==============================================================
  // LOGOUT
  // ==============================================================

  const handleLogout =
    async () => {
      await logout();
      navigate("/login");
    };

  // ==============================================================
  // MARCAR TODAS COMO LIDAS
  // ==============================================================

  const handleMarkAllRead =
    async () => {
      try {
        await api.patch(
          "/notifications/read-all",
        );

        setNotifications(
          (current) =>
            current.map(
              (notification) => ({
                ...notification,
                read: true,
              }),
            ),
        );

        toast.success(
          "Todas as notificações foram marcadas como lidas.",
        );
      } catch (error) {
        toast.error(
          "Não foi possível marcar as notificações como lidas.",
        );
      }
    };

  // ==============================================================
  // RENDER
  // ==============================================================

  const closeMenu = useCallback(() => setOpen(false), []);
  const openMenu = useCallback(() => setOpen(true), []);

  return (
    <div className="min-h-screen bg-background">
      {open && (
        <div
          className="fixed inset-0 bg-foreground/25 backdrop-blur-[2px] z-40 transition-opacity duration-300"
          onClick={closeMenu}
          data-testid="sidebar-overlay"
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          "fixed inset-y-0 left-0 z-50 w-72 max-w-[88vw] border-r border-border bg-card/95 backdrop-blur-xl",
          "shadow-2xl flex flex-col transform transition-transform duration-300 ease-out will-change-transform",
          open ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
        aria-hidden={!open}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
          {branding.logo_small || branding.logo_main ? (
            <img
              src={branding.logo_small || branding.logo_main}
              alt={branding.login_name}
              className="h-8 w-8 rounded-md object-cover"
            />
          ) : (
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <Bug className="text-primary-foreground" size={18} />
            </div>
          )}

          <div className="min-w-0">
            <p className="font-heading font-bold text-sm leading-none tracking-tight truncate">
              {branding.login_name}
            </p>
            <p className="text-[9px] text-muted-foreground tracking-[0.18em] mt-0.5 uppercase truncate">
              {branding.system_name}
            </p>
          </div>

          <button
            className="ml-auto h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-all duration-200"
            onClick={closeMenu}
            data-testid="sidebar-close-btn"
            title="Fechar menu"
            aria-label="Fechar menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-3 space-y-1">
          <NavGroup title="Operação" items={operacao} onNavigate={closeMenu} />

          {estrutura.length > 0 && (
            <div className="pt-2 mt-2 border-t border-border/60" />
          )}

          <NavGroup title="Estrutura" items={estrutura} onNavigate={closeMenu} />
        </nav>

        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
              {user.name?.[0]?.toUpperCase()}
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">
                {user.name}
              </p>
              <p className="text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</p>
            </div>

            <button
              onClick={() => setPwdOpen(true)}
              className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-primary transition-all duration-200"
              data-testid="change-password-btn"
              title="Alterar senha"
            >
              <KeyRound size={15} />
            </button>

            <button
              onClick={handleLogout}
              className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all duration-200"
              data-testid="logout-btn"
              title="Sair"
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>

        <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      </aside>

      <div className="min-h-screen flex flex-col min-w-0">
        {demoActive && (
          <div
            className="sticky top-0 z-40 h-9 flex items-center justify-center gap-2 bg-amber-500 text-amber-950 text-[11px] font-bold tracking-[0.15em] uppercase px-3"
            data-testid="demo-banner"
          >
            <FlaskConical size={13} className="shrink-0" />
            <span className="truncate">
              Demonstração pessoal
              {demo?.scenarios?.[demo.scenario]
                ? ` · ${demo.scenarios[demo.scenario]}`
                : ""}{" "}
              — só você vê estes dados fictícios; os demais usuários seguem nos dados reais
            </span>
          </div>
        )}

        <header
          className={[
            "sticky z-30 h-16 border-b border-border bg-background/85 backdrop-blur-xl",
            "flex items-center gap-2 px-4 sm:px-6",
            demoActive ? "top-9" : "top-0",
          ].join(" ")}
        >
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-foreground transition-all duration-200"
            onClick={openMenu}
            data-testid="sidebar-open-btn"
            title="Abrir menu"
            aria-label="Abrir menu"
            aria-expanded={open}
          >
            <Menu size={20} />
          </Button>

          <p className="hidden sm:block text-xs text-muted-foreground tracking-wide truncate">
            {branding.header_name}
          </p>

          <div className="flex-1" />

          <ClientSelector />

          <Popover
            open={notificationPanelOpen}
            onOpenChange={setNotificationPanelOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`relative transition-all duration-200 ${
                  unread > 0 ? "text-primary" : ""
                } ${!realtimeConnected ? "opacity-50" : ""}`}
                data-testid="notifications-btn"
              >
                <Bell size={18} className={unread > 0 ? "animate-pulse" : ""} />
                {unread > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-semibold"
                    data-testid="notifications-badge"
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Button>
            </PopoverTrigger>

            <PopoverContent
              align="end"
              sideOffset={10}
              className="w-[min(92vw,420px)] p-0 overflow-hidden"
              data-testid="notification-center"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-sm">Central de notificações</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {unread > 0
                      ? `${unread} não lida${unread === 1 ? "" : "s"}`
                      : "Tudo em dia"}
                    {" · "}
                    {realtimeConnected ? "tempo real" : "reconectando"}
                  </p>
                </div>
                {unread > 0 && (
                  <button
                    className="text-xs text-primary hover:underline shrink-0 ml-3"
                    data-testid="mark-all-read-btn"
                    onClick={handleMarkAllRead}
                  >
                    Marcar todas
                  </button>
                )}
              </div>

              <div className="px-4 py-2 border-b border-border/50 bg-muted/20 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] text-muted-foreground">Canal em tempo real</span>
                  <span className="text-[11px] font-medium">
                    <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 ${
                      realtimeConnected ? "bg-emerald-500" : "bg-amber-500 animate-pulse"
                    }`} />
                    {realtimeConnected ? "Conectado" : "Reconectando..."}
                  </span>
                </div>

                {notificationSystemPermission !== "granted" &&
                  notificationSystemPermission !== "unsupported" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full h-8 text-xs"
                      onClick={handleRequestBrowserPermission}
                      data-testid="enable-system-notifications-btn"
                    >
                      Ativar notificações do computador
                    </Button>
                  )}

                {notificationSystemPermission === "granted" && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-emerald-500">
                      Notificações do sistema autorizadas
                    </span>
                    <button
                      type="button"
                      className="text-[11px] text-primary hover:underline"
                      onClick={handleTestNotification}
                      data-testid="test-system-notification-btn"
                    >
                      Testar
                    </button>
                  </div>
                )}
              </div>

              <div className="max-h-[min(60vh,520px)] overflow-y-auto scrollbar-thin p-2 space-y-2 bg-background/80">
                {notifications.length === 0 && (
                  <div className="p-8 text-center" data-testid="notifications-empty">
                    <Bell size={22} className="mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma notificação.</p>
                  </div>
                )}

                {notifications.map((notification) => {
                  const priority = NOTIFICATION_PRIORITY[notificationPriority(notification)];

                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={[
                        "w-full text-left rounded-lg border px-3 py-3",
                        "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        priority.className,
                        notification.read ? "opacity-80" : "shadow-sm",
                      ].join(" ")}
                      data-testid="notification-item"
                    >
                      <div className="flex items-start gap-2.5">
                        <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${priority.dot} ${
                          notification.read ? "opacity-40" : ""
                        }`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm truncate ${
                              notification.read ? "font-medium" : "font-semibold"
                            }`}>
                              {notification.title}
                            </p>
                            <span className={`text-[9px] uppercase tracking-wider font-semibold shrink-0 ${priority.text}`}>
                              {priority.label}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-3">
                            {notification.body}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>

          <Popover
            open={settingsMenuOpen}
            onOpenChange={setSettingsMenuOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="settings-gear-btn"
                title="Configurações"
                className="transition-all duration-200"
              >
                <Cog size={18} />
              </Button>
            </PopoverTrigger>

            <PopoverContent align="end" className="w-80 p-0" data-testid="settings-menu">
              <div className="px-4 py-3 border-b border-border">
                <p className="font-heading font-semibold text-sm">Configurações</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Somente os grupos liberados para o seu perfil
                </p>
              </div>

              <div className="max-h-96 overflow-y-auto scrollbar-thin">
                {configItems.length === 0 && (
                  <p className="text-sm text-muted-foreground p-4" data-testid="settings-empty">
                    Nenhuma configuração liberada para o seu acesso atual.
                  </p>
                )}

                {showAdminCenter && (
                  <button
                    onClick={() => {
                      closeMenu();
                      navigate("/admin");
                    }}
                    className="w-full text-left px-4 py-2.5 border-b border-border/50 bg-primary/5 hover:bg-primary/10 transition-all duration-200 flex items-start gap-3"
                    data-testid="settings-item-admin-center"
                  >
                    <ShieldCheck size={15} className="text-primary mt-0.5 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">Central Administrativa</span>
                      <span className="block text-[11px] text-muted-foreground leading-snug">
                        Empresas, usuários, acessos, catálogo e lixeira
                      </span>
                    </span>
                  </button>
                )}

                {configItems.map(({ key, to, label, desc, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => {
                      closeMenu();
                      navigate(to);
                    }}
                    className="w-full text-left px-4 py-2.5 border-b border-border/50 last:border-0 hover:bg-accent transition-all duration-200 flex items-start gap-3"
                    data-testid={`settings-item-${key}`}
                  >
                    <Icon size={15} className="text-primary mt-0.5 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-[11px] text-muted-foreground leading-snug">
                        {desc}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

                    <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle-btn"
            className="transition-all duration-200"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </Button>

          {/* ===== MENU DE USUÁRIO — Meu Perfil / Segurança / Sair ===== */}
          <Popover
            open={userMenuOpen}
            onOpenChange={setUserMenuOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 h-9 px-2 pr-3 cursor-pointer"
                data-testid="user-menu-trigger"
                title={`${user.name || "Usuário"} — ${ROLE_LABELS[user.role] || ""}`}
              >
                <div className="h-7 w-7 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-xs">
                  {user.name?.[0]?.toUpperCase() || "?"}
                </div>
                <span className="hidden sm:block text-sm font-medium truncate max-w-[140px]">
                  {user.name || "Usuário"}
                </span>
                <span className="hidden sm:inline-block text-xs text-muted-foreground">
                  ({ROLE_LABELS[user.role] || ""})
                </span>
                <ChevronDown size={14} className="text-muted-foreground" />
              </Button>
            </PopoverTrigger>

            <PopoverContent align="end" className="w-56 p-0 border shadow-lg">
              <div className="p-3 border-b border-border">
                <p className="font-medium text-sm">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {user.email}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {ROLE_LABELS[user.role] || user.role}
                </p>
              </div>

              <div className="py-1">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors"
                                onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/meu-perfil");
                  }}
                  data-testid="menu-my-profile"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Meu Perfil
                </button>

                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setPwdOpen(true);
                  }}
                  data-testid="menu-change-password"
                >
                  <KeyRound size={14} />
                  Alterar senha
                </button>
              </div>

              <div
                className="border-t border-border py-1"
              >
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-destructive hover:bg-destructive/10 transition-colors"
                  onClick={() => {
                    setUserMenuOpen(false);
                    handleLogout();
                  }}
                  data-testid="menu-logout"
                >
                  <LogOut size={14} />
                  Sair
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </header>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          <div
            key={location.pathname}
            className="min-h-full animate-in fade-in slide-in-from-bottom-2 duration-300 ease-out"
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}